import React, { useState, useRef, useEffect } from "react";
import { TrendingUp, TrendingDown, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const OverallReports = ({ dashboardData, isLoading, error }) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle outside click for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calculatePercentage = (part, total) => {
    if (!total) return "0%";
    return `${Math.round((part / total) * 100)}%`;
  };

  // Handle missing or unauthorized data
  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading dashboard data...
      </div>
    );
  }

  if (error === 401 || error === "Unauthorized") {
    return (
      <div className="p-6 text-center text-red-500 font-medium">
        Unauthorized access. Please log in again.
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-6 text-center text-gray-500">
        No dashboard data available.
      </div>
    );
  }

  // Build report cards safely
  const reports = [
    {
      metric: "Total Users",
      value: dashboardData.users?.total?.toLocaleString() || "0",
      change: "",
      isPositive: true,
    },
    {
      metric: "Active Users",
      value: dashboardData.users?.active?.toLocaleString() || "0",
      change: calculatePercentage(
        dashboardData.users?.active,
        dashboardData.users?.total
      ),
      isPositive: true,
    },
    {
      metric: "Published Posts",
      value: dashboardData.posts?.published?.toLocaleString() || "0",
      change: calculatePercentage(
        dashboardData.posts?.published,
        dashboardData.posts?.total
      ),
      isPositive: true,
    },
    {
      metric: "Draft Posts",
      value: dashboardData.posts?.draft?.toLocaleString() || "0",
      change: calculatePercentage(
        dashboardData.posts?.draft,
        dashboardData.posts?.total
      ),
      isPositive: false,
    },
  ];

  // Theme-based classes
  const cardClasses = isDarkMode
    ? "bg-black border-zinc-900 text-white"
    : "bg-white border-gray-200 text-gray-900 shadow-lg";

  const headerTextClass = isDarkMode ? "text-white" : "text-gray-900";

  const dropdownButtonClasses = isDarkMode
    ? "bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
    : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100";

  const dropdownMenuClasses = isDarkMode
    ? "bg-zinc-900 border-zinc-800"
    : "bg-white border-gray-200 shadow-lg";

  const dropdownItemClasses = isDarkMode
    ? "hover:bg-zinc-800 text-gray-300"
    : "hover:bg-gray-100 text-gray-700";

  const dropdownItemActiveClasses = isDarkMode
    ? "bg-zinc-800 text-white"
    : "bg-blue-50 text-blue-600";

  const itemBgClasses = isDarkMode
    ? "bg-black hover:bg-zinc-900"
    : "bg-gray-50 hover:bg-gray-100";

  const metricTextClass = "text-gray-500";
  const valueTextClass = isDarkMode ? "text-white" : "text-gray-900";

  const getChangeColor = (isPositive) => {
    if (isPositive) {
      return isDarkMode ? "text-green-400" : "text-green-600";
    } else {
      return isDarkMode ? "text-red-400" : "text-red-600";
    }
  };

  return (
    <div
      className={`rounded-xl p-5 h-full border transition-colors duration-300 ${cardClasses}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className={`text-xl font-semibold ${headerTextClass}`}>
          Overall Reports
        </h3>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {reports.map((report, idx) => (
          <div
            key={idx}
            className={`flex items-center justify-between p-3 rounded-lg transition-colors ${itemBgClasses}`}
          >
            <div className="flex-1">
              <p className={`text-sm ${metricTextClass}`}>{report.metric}</p>
              <p className={`text-xl font-semibold mt-1 ${valueTextClass}`}>
                {report.value}
              </p>
            </div>

            <div
              className={`flex items-center gap-1 ${getChangeColor(
                report.isPositive
              )}`}
            >
              {report.change.includes("+") || report.change.includes("-") ? (
                <>
                  {report.isPositive ? (
                    <TrendingUp size={16} />
                  ) : (
                    <TrendingDown size={16} />
                  )}
                  <span className="text-sm font-medium">{report.change}</span>
                </>
              ) : (
                <span className={`text-sm font-medium ${metricTextClass}`}>
                  {report.change}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverallReports;
